import React from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Divider,
    ListSubheader,
    Box,
} from '@mui/material';
import {
    Palette as PaletteIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    SettingsBrightness as SystemIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../theme/ThemeContext';
import { ColorSchemeId, COLOR_SCHEMES } from '../theme/colorSchemes';

const ColorSwatch: React.FC<{ color: string; selected?: boolean }> = ({ color, selected }) => (
    <Box
        sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: color,
            border: selected ? '3px solid' : '2px solid',
            borderColor: selected ? 'text.primary' : 'divider',
        }}
    />
);

const AppearanceMenu: React.FC = () => {
    const { mode, setMode, isDark, colorScheme, setColorScheme } = useThemeMode();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectScheme = (schemeId: ColorSchemeId) => {
        setColorScheme(schemeId);
    };

    const handleSelectMode = (newMode: 'light' | 'dark' | 'system') => {
        setMode(newMode);
    };

    return (
        <>
            <Tooltip title="Vzhled">
                <IconButton
                    color="inherit"
                    onClick={handleClick}
                    aria-label="zmenit vzhled"
                    aria-controls={open ? 'appearance-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <PaletteIcon />
                </IconButton>
            </Tooltip>
            <Menu
                id="appearance-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'appearance-button',
                }}
            >
                <ListSubheader sx={{ bgcolor: 'background.paper' }}>Barvy</ListSubheader>
                {Object.values(COLOR_SCHEMES).map((scheme) => (
                    <MenuItem
                        key={scheme.id}
                        onClick={() => handleSelectScheme(scheme.id)}
                        selected={colorScheme === scheme.id}
                    >
                        <ListItemIcon>
                            <ColorSwatch
                                color={isDark ? scheme.colors.dark.primary : scheme.colors.light.primary}
                                selected={colorScheme === scheme.id}
                            />
                        </ListItemIcon>
                        <ListItemText>{scheme.name}</ListItemText>
                    </MenuItem>
                ))}

                <Divider sx={{ my: 1 }} />

                <ListSubheader sx={{ bgcolor: 'background.paper' }}>Rezim</ListSubheader>
                <MenuItem
                    onClick={() => handleSelectMode('light')}
                    selected={mode === 'light'}
                >
                    <ListItemIcon>
                        <LightModeIcon />
                    </ListItemIcon>
                    <ListItemText>Svetly</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => handleSelectMode('dark')}
                    selected={mode === 'dark'}
                >
                    <ListItemIcon>
                        <DarkModeIcon />
                    </ListItemIcon>
                    <ListItemText>Tmavy</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => handleSelectMode('system')}
                    selected={mode === 'system'}
                >
                    <ListItemIcon>
                        <SystemIcon />
                    </ListItemIcon>
                    <ListItemText>Systemovy</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

export default AppearanceMenu;
